import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material'
import { CalendarToday, CheckCircle, Cancel, Info } from '@mui/icons-material'
import { apiService } from '../../services/api'

const SalonAvailabilityModal = ({ open, onClose, operatorCode, plantCode, salonName }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [availabilityData, setAvailabilityData] = useState(null)
  const [perProductData, setPerProductData] = useState(null)
  const [showPerProduct, setShowPerProduct] = useState(false)

  useEffect(() => {
    if (open && operatorCode && plantCode) {
      fetchAvailabilityData()
    }
  }, [open, operatorCode, plantCode])

  const fetchAvailabilityData = async () => {
    setLoading(true)
    setError(null)
    try {
      // For PLUS and P4 (Play) operators, use per-product view
      if (operatorCode === 'PLUS' || operatorCode === 'P4') {
        const perProductResponse = await apiService.getSalonAvailabilityDetailsPerProduct(operatorCode, plantCode, 30)
        setPerProductData(perProductResponse.data)
        setShowPerProduct(true)
      } else {
        // For other operators, use regular availability data
        const response = await apiService.getSalonAvailabilityDetails(operatorCode, plantCode, 30)
        setAvailabilityData(response.data)
        setShowPerProduct(false)
      }
    } catch (err) {
      console.error('Error fetching availability data:', err)
      setError('Błąd podczas ładowania szczegółów dostępności')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    const today = new Date()
    const isToday = date.toDateString() === today.toDateString()
    
    return {
      formatted: date.toLocaleDateString('pl-PL', { 
        weekday: 'short', 
        day: '2-digit', 
        month: '2-digit' 
      }),
      isToday
    }
  }

  const getAvailabilityColor = (available) => {
    return available ? 'success' : 'error'
  }

  const getAvailabilityIcon = (available) => {
    return available ? <CheckCircle sx={{ fontSize: 16 }} /> : <Cancel sx={{ fontSize: 16 }} />
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center">
          <CalendarToday sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            Szczegóły dostępności - {salonName || plantCode}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Plant Code: {plantCode} | Ostatnie 30 dni kalendarzowych
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Ładowanie danych dostępności...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (perProductData || availabilityData) ? (
          <>
            {/* Summary Stats */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                📊 Podsumowanie
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2}>
                {perProductData ? (
                  <>
                    <Chip 
                      label={`${(perProductData.overall_availability_pct_30d * 100).toFixed(1)}% dostępności (średnia)`}
                      color={perProductData.overall_availability_pct_30d >= 0.8 ? 'success' : 
                             perProductData.overall_availability_pct_30d >= 0.5 ? 'warning' : 'error'}
                    />
                    <Chip 
                      icon={<Info />}
                      label={`${perProductData.products.length} produktów`}
                      color="primary"
                      variant="outlined"
                    />
                    {perProductData.requires_special_attention && (
                      <Chip 
                        label="Wymaga specjalnej uwagi"
                        color="error"
                        size="small"
                      />
                    )}
                  </>
                ) : (
                  <>
                    <Chip 
                      icon={<Info />}
                      label={`${availabilityData.available_days}/${availabilityData.total_days} dni z towarem`}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip 
                      label={`${Number(availabilityData.availability_percentage).toFixed(1)}% dostępności`}
                      color={availabilityData.availability_percentage >= 80 ? 'success' : 
                             availabilityData.availability_percentage >= 50 ? 'warning' : 'error'}
                    />
                    {availabilityData.requires_special_attention && (
                      <Chip 
                        label="Wymaga specjalnej uwagi"
                        color="error"
                        size="small"
                      />
                    )}
                  </>
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Calculation Explanation */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="info.dark" gutterBottom>
                💡 Jak liczona jest dostępność?
              </Typography>
              <Typography variant="body2" color="info.dark">
                <strong>% dostępności</strong> = (dni dostępne) / (łącznie dni obserwowanych) × 100<br/>
                <strong>Dzień dostępny</strong> = stock dziś {'>'} 0<br/>
                <strong>Stock dziś</strong> = (stock z ostatniego snapshot) - (sprzedaż od snapshot do dziś)<br/>
                <br/>
                <strong>Kolumny:</strong><br/>
                • <strong>Data snap</strong> - data ostatniego snapshot przed danym dniem<br/>
                • <strong>Stock (snap)</strong> - stan z tego snapshot<br/>
                • <strong>Sprzed. od snap</strong> - łączna sprzedaż od snapshot do dnia<br/>
                • <strong>Stock dziś</strong> = Stock(snap) - Sprzed.(od snap)<br/>
                • <strong>Sprzed. dziś</strong> - sprzedaż tylko w tym dniu
              </Typography>
            </Box>

            {/* Daily Breakdown Table */}
            {perProductData ? (
              <>
                <Typography variant="h6" gutterBottom>
                  📅 Szczegóły per-product
                </Typography>
                {perProductData.products.map((product, index) => (
                  <Box key={product.product_name} sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ 
                      color: product.product_name === 'Opaska Senior' ? 'primary.main' : 'secondary.main',
                      fontWeight: 'bold'
                    }}>
                      {product.product_name} - {product.availability_percentage.toFixed(1)}% dostępności
                    </Typography>
                    
                    <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Data</strong></TableCell>
                            <TableCell align="center"><strong>Dostępny</strong></TableCell>
                            <TableCell align="center"><strong>Data snap</strong></TableCell>
                            <TableCell align="right"><strong>Stock (snap)</strong></TableCell>
                            <TableCell align="right"><strong>Sprzed. od snap</strong></TableCell>
                            <TableCell align="right"><strong>Stock dziś</strong></TableCell>
                            <TableCell align="right"><strong>Sprzed. dziś</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {product.daily_data.map((day, dayIndex) => {
                            const { formatted, isToday } = formatDate(day.date)
                            return (
                              <TableRow 
                                key={dayIndex}
                                sx={{ 
                                  backgroundColor: isToday ? 'action.hover' : 'inherit',
                                  '&:hover': { backgroundColor: 'action.selected' }
                                }}
                              >
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CalendarToday sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                    <Typography 
                                      variant="body2" 
                                      fontWeight={isToday ? 'bold' : 'normal'}
                                      color={isToday ? 'primary.main' : 'inherit'}
                                    >
                                      {formatted}
                                    </Typography>
                                    {isToday && (
                                      <Chip 
                                        label="DZIŚ" 
                                        color="primary" 
                                        size="small"
                                        sx={{ ml: 1 }}
                                      />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {day.available ? (
                                      <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                    ) : (
                                      <Cancel sx={{ color: 'error.main', fontSize: 20 }} />
                                    )}
                                    <Chip
                                      label={day.available ? 'TAK' : 'NIE'}
                                      color={day.available ? 'success' : 'error'}
                                      size="small"
                                      sx={{ ml: 1 }}
                                    />
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                    {day.snapshot_date ? formatDate(day.snapshot_date).formatted : 'Brak'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography 
                                    variant="body2"
                                    color={day.quantity > 0 ? 'text.primary' : 'text.secondary'}
                                    fontWeight={day.quantity > 0 ? 'medium' : 'normal'}
                                  >
                                    {day.quantity.toLocaleString()}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography 
                                    variant="body2"
                                    color={day.cumulative_sales_since_snapshot > 0 ? 'info.main' : 'text.secondary'}
                                    fontWeight={day.cumulative_sales_since_snapshot > 0 ? 'medium' : 'normal'}
                                  >
                                    {day.cumulative_sales_since_snapshot || 0}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography 
                                    variant="body2"
                                    color={day.estimated_stock_today > 0 ? 'success.main' : 'error.main'}
                                    fontWeight="bold"
                                  >
                                    {day.estimated_stock_today?.toLocaleString() || 0}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography 
                                    variant="body2"
                                    color={day.daily_sales > 0 ? 'warning.main' : 'text.secondary'}
                                    fontWeight={day.daily_sales > 0 ? 'bold' : 'normal'}
                                  >
                                    {day.daily_sales || 0}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </>
            ) : (
              <>
                <Typography variant="h6" gutterBottom>
                  📅 Szczegóły dzień po dzień
                </Typography>
            
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Data</strong></TableCell>
                    <TableCell align="center"><strong>Dostępny</strong></TableCell>
                    <TableCell align="center"><strong>Data snap</strong></TableCell>
                    <TableCell align="right"><strong>Stock (snap)</strong></TableCell>
                    <TableCell align="right"><strong>Sprzed. od snap</strong></TableCell>
                    <TableCell align="right"><strong>Stock dziś</strong></TableCell>
                    <TableCell align="right"><strong>Sprzed. dziś</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {availabilityData.daily_data.map((day, index) => {
                    const { formatted, isToday } = formatDate(day.date)
                    return (
                      <TableRow 
                        key={index}
                        sx={{ 
                          bgcolor: isToday ? 'primary.light' : 'inherit',
                          '&:nth-of-type(odd)': { bgcolor: isToday ? 'primary.light' : 'action.hover' }
                        }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            {formatted}
                            {isToday && (
                              <Chip 
                                label="Dziś" 
                                size="small" 
                                color="primary" 
                                sx={{ ml: 1, fontSize: '0.75rem' }} 
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center">
                            {getAvailabilityIcon(day.available)}
                            <Chip
                              label={day.available ? 'TAK' : 'NIE'}
                              color={getAvailabilityColor(day.available)}
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                            {day.snapshot_date ? formatDate(day.snapshot_date).formatted : 'Brak'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2"
                            color={day.quantity > 0 ? 'text.primary' : 'text.secondary'}
                            fontWeight={day.quantity > 0 ? 'medium' : 'normal'}
                          >
                            {(day.quantity || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2"
                            color={day.cumulative_sales_since_snapshot > 0 ? 'info.main' : 'text.secondary'}
                            fontWeight={day.cumulative_sales_since_snapshot > 0 ? 'medium' : 'normal'}
                          >
                            {day.cumulative_sales_since_snapshot || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2"
                            color={day.estimated_stock_today > 0 ? 'success.main' : 'error.main'}
                            fontWeight="bold"
                          >
                            {day.estimated_stock_today?.toLocaleString() || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2"
                            color={day.daily_sales > 0 ? 'warning.main' : 'text.secondary'}
                            fontWeight={day.daily_sales > 0 ? 'bold' : 'normal'}
                          >
                            {day.daily_sales || 0}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
              </>
            )}
          </>
        ) : (
          <Alert severity="info">
            Brak danych dostępności dla tego salonu.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Zamknij
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SalonAvailabilityModal
